import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Meta } from 'src/components/Meta';
import { Leaderboard } from 'src/models/leaderboard/_types';
import {
  createLeaderboard,
  fetchLeaderboard,
  fetchLeaderboards,
  updateLeaderboard,
} from 'src/models/leaderboard/leaderboard';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { Identifier } from 'src/types/utilityTypes';
import toast from 'src/utils/toast';

import { LeaderboardForm } from './LeaderboardForm';

export const LeaderboardFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isNew = !id;

  const { data: leaderboard } = useQuery({
    queryKey: ['leaderboard', id],
    queryFn: () => fetchLeaderboard(id as Identifier),
    enabled: !isNew,
  });

  const { data: leaderboardsData } = useQuery({
    queryKey: ['leaderboards', { page: 1 }],
    queryFn: () => fetchLeaderboards({ page: 1, perPage: 100 }),
  });

  const otherLeaderboards = leaderboardsData?.leaderboard.filter(
    (l) => l.id.toString() !== id?.toString()
  );

  const createMutation = useMutation({
    mutationFn: createLeaderboard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaderboards'] });
      toast.success('Leaderboard created successfully');
    },
    onError: () => {
      toast.error('Failed to create leaderboard');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: Leaderboard) => updateLeaderboard(values.id, values),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaderboards'] });
      toast.success('Leaderboard updated successfully');
    },

    onError: () => {
      toast.error('Failed to update leaderboard');
    },
  });

  const handleSubmit = async (values: Leaderboard) => {
    if (isNew) {
      await createMutation.mutateAsync(values);
    } else {
      await updateMutation.mutateAsync(values);
    }
  };

  if (!isNew && !leaderboard?.id) {
    return null;
  }

  return (
    <>
      <Meta title={isNew ? 'New Leaderboard' : 'Edit Leaderboard'} />
      <AppFrameConfig
        id="leaderboard-form"
        containerMaxWidth="xl"
        title={isNew ? 'New Leaderboard' : 'Edit Leaderboard'}
      />
      <LeaderboardForm
        onSubmit={handleSubmit}
        leaderboard={leaderboard}
        otherLeaderboards={otherLeaderboards || []}
      />
    </>
  );
};
